import { WidgetHeader } from 'legacy/lut/components/widgets/public/partner360/widgetHeader/WidgetHeader'
import { Table } from 'legacy/lut/library/list'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { Translation } from 'cng-common/features/translations/components/translation'
import { deleteParamsFromUrl, getUrlWithParams } from 'core-components/helpers/url'
import { Button } from 'core-components/library/button'
import { Switch } from 'core-components/library/checkbox'
import { LoaderOverlay } from 'core-components/library/loader'
import { Static } from 'core-components/types'
import { Crud } from 'core/types/Crud'

import { fetchPartnersCompleteness } from '../../../services/fetchPartnersCompleteness'
import { getReplaceParams } from '../../helpers/getReplaceParams'
import { ICompletedPartners } from '../../models/ICompletedPartners'
import { IPartnersCompleteness } from '../../models/IPartnersCompleteness'

import { PLANNING_TAB, PLANNING_TAB_URL } from './config'
import { getColumns } from './helpers/getColumns'
import tr from './translations'

import styles from './PartnersCompletenessDialogContent.module.scss'

interface IProps {
  partnersCompleteness?: IPartnersCompleteness
  onClose: () => void
}

export const PartnersCompletenessDialogContent = ({ partnersCompleteness, onClose }: IProps) => {
  /** -------------------- State ----------------------- */

  const [foundPartners, setFoundPartners] = useState<ICompletedPartners[]>([])
  const [isLoaded, setIsLoaded] = useState(true)
  const [isShowCompletedPartners, setIsShowCompletedPartners] = useState(false)

  /** -------------------- Variables ----------------------- */

  let translationSuffix = partnersCompleteness?.isAcknowledged ? '-ack' : '-all'
  if (partnersCompleteness?.isCompleted) {
    translationSuffix = '-fully'
  }

  const replaceParams = getReplaceParams(partnersCompleteness)
  const switcherTranslation =
    (isShowCompletedPartners ? tr.showNotCompleted : tr.showCompleted) + translationSuffix
  const infoTranslation =
    (isShowCompletedPartners ? tr.infoNotCompleted : tr.infoCompleted) + translationSuffix

  /** -------------------- Methods ----------------------- */

  const refreshPartners = async (partnersCompleteness: IPartnersCompleteness) => {
    setIsLoaded(false)

    const partnersResponse =
      await fetchPartnersCompleteness<ICompletedPartners>(partnersCompleteness)
    const partners = partnersResponse?.Rows || []
    setFoundPartners(partners)

    setIsLoaded(true)
  }

  /** -------------------- User actions handlers ----------------------- */

  const onOpenPartnerPlan = useCallback((item: ICompletedPartners) => {
    const partnerId = item.PartnerID

    const programType = partnersCompleteness?.programType || ''
    const linkWithoutDeletedParams = deleteParamsFromUrl(['pid'])
    const url = getUrlWithParams(
      {
        page: 'jbl-hpscoutso',
        pid: partnerId,
        ptype: programType,
        [PLANNING_TAB_URL]: PLANNING_TAB,
      },
      linkWithoutDeletedParams
    )

    window.open(url, '_blank')
  }, [])

  /** -------------------- Effects ----------------------- */

  const partnersCrud = useMemo(() => {
    const filteredPartners = foundPartners.filter((el) =>
      isShowCompletedPartners ? el['IsCompleted'] : !el['IsCompleted']
    )
    return new Crud({ fetcher: new Static(filteredPartners), keyField: 'PartnerID' })
  }, [foundPartners, isShowCompletedPartners])

  const editorColumns = useMemo(() => getColumns({ onOpenPartnerPlan }), [onOpenPartnerPlan])

  useEffect(() => {
    setFoundPartners([])

    if (partnersCompleteness) {
      refreshPartners(partnersCompleteness)
    }
  }, [partnersCompleteness])

  /** -------------------- Render ----------------------- */

  return (
    <div className={styles.root}>
      <WidgetHeader
        content={() => (
          <>
            &nbsp;
            <Translation span text={tr.subTitle} replaceParams={replaceParams} />
          </>
        )}
      >
        {tr.title}
      </WidgetHeader>

      <LoaderOverlay show={!isLoaded} />

      <div className={styles.contentContainer}>
        {!partnersCompleteness?.isSob && (
          <div className={styles.switchersContainer}>
            <div className={styles.switcher}>
              <Switch
                padding
                checked={isShowCompletedPartners}
                onChange={() => setIsShowCompletedPartners((val) => !val)}
              />
              <div className={styles.switcherText}>
                <Translation span text={switcherTranslation} replaceParams={replaceParams} />
              </div>
            </div>
            <Translation text={infoTranslation} replaceParams={replaceParams} htmlAvailable />
          </div>
        )}

        <Table<ICompletedPartners>
          pagination
          header
          columns={editorColumns}
          source={partnersCrud}
          hasTranslate
        />
      </div>

      <div className={styles.bottomContainer}>
        <Button onClick={onClose}>
          <Translation text={tr.closeButton} />
        </Button>
      </div>
    </div>
  )
}
